import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  isStaleChunkError,
  attemptStaleChunkReload,
  clearStaleChunkReloadCounter,
} from '@/utils/stale-chunk-reload';

interface GlobalErrorHandlerProps {
  children: ReactNode;
  onError?: (error: Error | string) => void;
}

const TOAST_DURATION = 5000;

const toastStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  background: '#323232',
  color: '#fff',
  padding: '12px 24px',
  borderRadius: 8,
  fontSize: 14,
  zIndex: 99999,
  pointerEvents: 'none',
  textAlign: 'center',
  maxWidth: 'calc(100vw - 48px)',
};

const GlobalErrorHandler: React.FC<GlobalErrorHandlerProps> = ({
  children,
  onError,
}) => {
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const showToast = (message: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(message);
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_DURATION);
  };

  // App booted successfully — clear any stale reload counter
  useEffect(() => {
    clearStaleChunkReloadCounter();
  }, []);

  // Clean up toast timer on unmount
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global Error:', event.error);

      if (event.message && isStaleChunkError(event.message)) {
        if (attemptStaleChunkReload()) return;
        showToast(
          'A new version is available. Please close and reopen the app.'
        );
        return;
      }

      if (
        event.message?.includes('NetworkError') ||
        event.message?.includes('Failed to fetch')
      ) {
        showToast('Network error occurred. Please check your connection.');
      } else {
        showToast('An unexpected error occurred.');
      }

      onError?.(event.error || event.message);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled Promise Rejection:', event.reason);

      const reason = event.reason;
      const reasonMessage = reason?.message || String(reason || '');
      if (
        reason?.name === 'ChunkLoadError' ||
        isStaleChunkError(reasonMessage)
      ) {
        if (attemptStaleChunkReload()) return;
        showToast(
          'A new version is available. Please close and reopen the app.'
        );
        return;
      }

      showToast('An error occurred while processing your request.');

      onError?.(event.reason);
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, [onError]);

  return (
    <>
      {children}
      {toast && <div style={toastStyle}>{toast}</div>}
    </>
  );
};

export default GlobalErrorHandler;
